import ProductWidgetContainer from "./ProductWidgets/ProductWidgetContainer";
import OrchestratorProductWidget from "./ProductWidgets/OrchestratorProductWidget";
import DocumentUnderstandingWidget from "./ProductWidgets/DocumentUnderstandingWidget";
import JobBasedProductWidget from "./ProductWidgets/JobBasedProductWidget";
import GenericUsersProductWidget from "./ProductWidgets/GenericUsersProductWidget";
import AutomationSuiteRobotsProductWidget from "./ProductWidgets/AutomationSuiteRobotsProductWidget";
import ProcessMiningProductWidget from "./ProductWidgets/ProcessMiningProductWidget";
import LLMGatewayProductWidget from "./ProductWidgets/LLMGatewayProductWidget";
import MaestroProductWidget from "./ProductWidgets/MaestroProductWidget";
import StudioWebProductWidget from "./ProductWidgets/StudioWebProductWidget";
import { SectionHeading } from "../RecommendationWidget/RecommendationWidget";
import styled from "styled-components/macro";
import PlatformProductWidget from "./ProductWidgets/PlatformProductWidget";
import * as constants from "../../../constants/calculatorConstants";
import { getDistribution } from "../../../adapters/distributionHelper";
import { getProductDependents, resolveServices } from "../../../adapters/dependencyAdapter";
import React from "react";

const SectionHeadingStyled = styled(SectionHeading)`
  margin-bottom: 10px;
`;

const AddProductSection = ({
  selectedProducts,
  onSelectedProductsChange,
  baselineProfile,
  deploymentType,
  explicitKeys,
}: {
  selectedProducts: ProductOptionInterface[];
  onSelectedProductsChange: (data: ProductOptionInterface[], explicitKeys: string[]) => void;
  baselineProfile: string;
  deploymentType: string;
  explicitKeys: string[];
}) => {
  // Scaling inputs are editable only outside the calibrated "default" reference
  // config. In default profiles inputs are read-only and DISPLAY the effective
  // (calibrated/constant) value — the same value the calculation uses.
  const editable = baselineProfile !== 'default';
  const isRemovable = (product: ProductOptionInterface): boolean => {
    const selectedKeys = selectedProducts.map((p: any) => p.key);
    const distribution = getDistribution(deploymentType);
    // Platform is always locked
    if (product.key === 'platform') return false;
    // Auto-selected products (not explicitly chosen) can't be removed
    if (!explicitKeys.includes(product.key)) return false;
    // Products with dependents can't be removed
    const dependents = getProductDependents(product.key, selectedKeys, distribution);
    if (dependents.length > 0) return false;
    return true;
  };

  const handleRemoveProduct = (selectedProduct: ProductOptionInterface) => {
    if (selectedProduct && selectedProduct.canBeRemoved) {
      const updatedExplicitKeys = explicitKeys.filter(k => k !== selectedProduct.key);
      const distribution = getDistribution(deploymentType);
      const resolved = resolveServices(updatedExplicitKeys, distribution);
      const required = new Set<string>(updatedExplicitKeys);
      resolved.autoSelectedProducts.forEach(k => required.add(k));

      // Keep existing order — just filter out products no longer required
      const updatedProducts = selectedProducts.filter(p => required.has(p.key));

      onSelectedProductsChange(updatedProducts, updatedExplicitKeys);
    }
  };

  const handleProductChange = (updatedProduct: ProductOptionInterface) => {
    const updateIndex = selectedProducts.findIndex(
      (currentProduct) => currentProduct.key === updatedProduct.key
    );
    if (updateIndex > -1) {
      selectedProducts[updateIndex] = updatedProduct;
      onSelectedProductsChange(selectedProducts, explicitKeys);
    }
  };

  const renderProducts = () => {
    const selectedKeys = selectedProducts.map((p: any) => p.key);

    // Platform special case: external orchestrator + other services
    let isOtherServiceSelected = selectedProducts.findIndex(
      (option: any) => {
        return (option.key === constants.BUSINESS_APPS_PRODUCT_OPTION.key
          || option.key === constants.AUTOMATION_HUB_PRODUCT_OPTION.key
          || option.key === constants.AUTOMATION_OPS_PRODUCT_OPTION.key
          || option.key === constants.DATA_SERVICE_PRODUCT_OPTION.key
          || option.key === constants.PROCESS_MINING_PRODUCT_OPTION.key);
      }) >= 0;
    let isAiCenterSelected = selectedKeys.includes(constants.AI_CENTER_PRODUCT_OPTION.key);
    let isExternalOrchestratorEnabled = false;
    if (isAiCenterSelected) {
      let aiCenterProduct = selectedProducts.filter(p => p.key == constants.AI_CENTER_PRODUCT_OPTION.key)[0];
      if (aiCenterProduct.properties !== undefined) {
        isExternalOrchestratorEnabled = !!aiCenterProduct.properties["externalOrchestrator"];
      }
    }

    // if DU AI Center variant is selected, AIC is required
    let isAICRequired = selectedProducts.some(p => p.key === constants.DOCUMENT_UNDERSTANDING_AI_CENTER_PRODUCT_OPTION.key);
    // if AIC is required and AIC is not selected, then add AIC to selected products
    if (isAICRequired && !isAiCenterSelected) {
      selectedProducts.push(constants.AI_CENTER_PRODUCT_OPTION);
    }

    const renderWidgetInner = (product: any): React.ReactNode => {
      // Compute removability dynamically
      let canBeRemoved = isRemovable(product);
      // Platform special case: allow removal when external orchestrator enabled and no other services
      if (product.key === "platform" && isExternalOrchestratorEnabled && !isOtherServiceSelected) {
        canBeRemoved = true;
      }
      // AI Center special case: lock if DU AI Center variant requires it
      if (product.key === "ai_center" && isAICRequired) {
        canBeRemoved = false;
      }
      // Numeric display is handled inside DraftNumberInput (effective at rest);
      // stored properties pass through unchanged.
      const updatedProduct = { ...product, canBeRemoved };

      switch (product.key) {
        case "platform":
          return (
            <PlatformProductWidget
              key={product.key}
              product={updatedProduct}
              onRemove={() => handleRemoveProduct(updatedProduct)}
              onProductChange={handleProductChange}
              editable={editable}
              baselineProfile={baselineProfile}
              deploymentType={deploymentType}
            />
          );
        case "orchestrator":
          return (
            <OrchestratorProductWidget
              key={product.key}
              product={updatedProduct}
              onRemove={() => handleRemoveProduct(updatedProduct)}
              onProductChange={handleProductChange}
              editable={editable}
              baselineProfile={baselineProfile}
              deploymentType={deploymentType}
            />
          );
        case "ai_center":
          return (
            <JobBasedProductWidget
              key={product.key}
              product={updatedProduct}
              onRemove={() => handleRemoveProduct(updatedProduct)}
              onProductChange={handleProductChange}
              editable={editable}
              baselineProfile={baselineProfile}
              deploymentType={deploymentType}
            />
          );
        case "document_understanding_ai_center":
        case "document_understanding_modern_projects":
          return (
            <DocumentUnderstandingWidget
              key={product.key}
              product={updatedProduct}
              onRemove={() => handleRemoveProduct(updatedProduct)}
              onProductChange={handleProductChange}
              editable={editable}
              baselineProfile={baselineProfile}
              deploymentType={deploymentType}
            />
          );
        // apps/semantic_proxy are calc-inert (no handler yet) but share the
        // standard users widget; their calibrated 15/50 floors drive the display.
        case "apps":
        case "semantic_proxy":
        case "action_center":
        case "automation_hub":
        case "automation_ops":
        case "data_service":
        case "test_manager":
          return (
            <GenericUsersProductWidget
              key={product.key}
              product={updatedProduct}
              onRemove={() => handleRemoveProduct(updatedProduct)}
              onProductChange={handleProductChange}
              editable={editable}
              baselineProfile={baselineProfile}
              deploymentType={deploymentType}
            />
          );
        case "automation_suite_robots":
          return (
            <AutomationSuiteRobotsProductWidget
                key={product.key}
                product={updatedProduct}
                onRemove={() => handleRemoveProduct(updatedProduct)}
                onProductChange={handleProductChange}
                editable={editable}
                baselineProfile={baselineProfile}
                deploymentType={deploymentType}
            />
          );
        case "process_mining":
          return (
            <ProcessMiningProductWidget
                key={product.key}
                product={updatedProduct}
                onRemove={() => handleRemoveProduct(updatedProduct)}
                onProductChange={handleProductChange}
                editable={editable}
                baselineProfile={baselineProfile}
                deploymentType={deploymentType}
            />
          );
        case "maestro":
          return (
            <MaestroProductWidget
                key={product.key}
                product={updatedProduct}
                onRemove={() => handleRemoveProduct(updatedProduct)}
                onProductChange={handleProductChange}
                editable={editable}
                baselineProfile={baselineProfile}
                deploymentType={deploymentType}
            />
          );
        case "aitl":
          return (
            <LLMGatewayProductWidget
                key={product.key}
                product={updatedProduct}
                onRemove={() => handleRemoveProduct(updatedProduct)}
                onProductChange={handleProductChange}
                editable={editable}
                baselineProfile={baselineProfile}
                deploymentType={deploymentType}
            />
          );
        case "studio_web":
          return (
            <StudioWebProductWidget
                key={product.key}
                product={updatedProduct}
                onRemove={() => handleRemoveProduct(updatedProduct)}
                onProductChange={handleProductChange}
                editable={editable}
                baselineProfile={baselineProfile}
                deploymentType={deploymentType}
            />
          );
        default:
          return (
            <ProductWidgetContainer
              key={product.key}
              productKey={product.key}
              title={product.name}
              onRemove={() => handleRemoveProduct(updatedProduct)}
              canBeRemoved={canBeRemoved}
              productProfile={product.profile}
              deploymentType={deploymentType}
              onProfileChange={(newProfile) => {
                handleProductChange({ ...product, profile: newProfile });
              }}
            />
          );
      }
    };

    return selectedProducts.map((product: any) => renderWidgetInner(product));
  };

  return (
    <div>
      <SectionHeadingStyled>Product selection</SectionHeadingStyled>
      {renderProducts()}
    </div>
  );
};

export default AddProductSection;
